import React, { useState, useRef, useEffect, useCallback } from 'react';
import gameAssets from '@themed-assets';
import {
  GAME_TYPE_ABBR,
  REENTRY_TYPE_ABBR,
  TOURNAMENT_TYPE_ABBR,
  BOUNTY_TYPE_ABBR,
  TOURNEY_ANNOUNCED,
  TOURNEY_LEGENDS,
  TOURNEY_OFFLINE_PAUSED,
  TOURNEY_PLAYER_STATUS_RE_ENTRY,
  TOURNEY_REGISTRATION_ENDED,
  TOURNEY_REGISTRATION_STARTED,
  TOURNEY_STATUS_CANCELLED,
  TOURNEY_STATUS_COMPLETED,
  TOURNEY_STATUS_LIVE,
  TOURNEY_STATUS_MISSED,
  TOURNEY_STATUS_WON
} from '@/Poker/data/Constants';
import useGetSingleTourneyDefinitionById from '@/src/hooks/data/useGetSingleTourneyDefinitionById';
import { TourneyTimer } from './TourneyTimer';
import useGetAllTourneyTickets from '@/src/hooks/data/useGetAllTourneyTickets';
import useTourneyDetails from '@/src/hooks/utils/useTourneyDetails';
import { formatAmountWithCommas } from '@/src/hooks/utils/utilsFun';
import Register from './Register';

const BottomModal = ({ open, onClose, children }) => {
  const modalRef = useRef(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000]">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />

      <div
        ref={modalRef}
        className="absolute right-0 bottom-0 left-0 flex max-h-[80vh] min-h-[200px] flex-col p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const TourneyLegendItem = ({ code, label, description }) => (
  <div className="pt-4">
    <div className="flex items-center gap-2">
      <div className="bg-accent text-info rounded-200 px-2 py-1 text-xs font-bold">
        {code}
      </div>
      <div className="text-primary text-base font-semibold">{label}</div>
    </div>
    <div className="text-secondary pt-1 text-sm">{description}</div>
  </div>
);

const A23TourneyTile = ({ game, isLateReg, status }) => {
  const [open, setOpen] = useState(false);
  const singleTourneyId = useGetSingleTourneyDefinitionById(game);
  // console.log('singleTourneyId-->', singleTourneyId?.tourneyStatus);

  const tourneyDetails = useTourneyDetails();
  const getAllTourneyTickets = useGetAllTourneyTickets();

  const gameType = GAME_TYPE_ABBR(singleTourneyId?.gameType);
  const tournamentType = TOURNAMENT_TYPE_ABBR(singleTourneyId?.tournamentType);
  const reEntryType = REENTRY_TYPE_ABBR(singleTourneyId?.reEntryType);
  const bountyType = BOUNTY_TYPE_ABBR(singleTourneyId?.bountyTourneyType);

  const TOURNEY_CARD_TYPES = [
    gameType,
    tournamentType,
    reEntryType,
    bountyType
  ];

  const checkTourneyGameTableIsAvailable = () => {
    try {
      let isAlreadyActivePlayer = false;
      const tourneyId = singleTourneyId?.tourneyDefinitionId || game;

      return isAlreadyActivePlayer;
    } catch (e) {
      console.log('ERROR AT JOIN BACK', e);
      return false;
    }
  };

  const getTourneyTileDynamicCtaOrStatus = useCallback(
    (status) => {
      if (
        singleTourneyId?.isFlightTourney &&
        singleTourneyId?.isFlightParentTourney &&
        singleTourneyId?.tourneyIdentifier !== 'completedTourneys'
      ) {
        return (
          <div className="from-card-grad-orange-300 to-card-grad-orange-700 text-foreground rounded-200 min-h-[24px] min-w-[96px] bg-gradient-to-b px-3 py-1 text-center text-xs font-bold">
            View Series
          </div>
        );
      }
      switch (status) {
        case TOURNEY_ANNOUNCED:
          return (
            singleTourneyId?.registrationStartTime && (
              <div>
                <div className="border-b-main rounded-200 flex min-h-[24px] min-w-[111px] flex-col justify-center border-1 px-3 py-1 text-center text-xs">
                  Announced
                </div>
              </div>
            )
          );
        case TOURNEY_REGISTRATION_STARTED:
        case TOURNEY_REGISTRATION_ENDED:
          if (singleTourneyId?.isAlreadyRegisteredTourney) {
            return (
              <div className="border-cta rounded-200 flex h-[40px] min-w-[96px] flex-col items-center justify-center border-1 border-solid text-xs">
                <img
                  src={gameAssets.registeredTick}
                  alt="Tourney tick"
                  height={16}
                  width={16}
                  className="object-cover"
                />
                <p className="text-info text-xs font-bold">Registered</p>
              </div>
            );
          } else if (
            singleTourneyId?.tournamentType === TOURNAMENT_TYPE_ABBR.TICKET ||
            tourneyDetails?.checkUserHasTicket(singleTourneyId?.tourneyId)
          ) {
            return tourneyDetails?.checkUserHasTicket(
              singleTourneyId?.tourneyId
            ) ? (
              <Register singleTourneyId={singleTourneyId} isTicket={true} />
            ) : (
              <div className="border-b-main rounded-200 flex min-h-[24px] min-w-[111px] flex-col justify-center border-1 px-3 py-1 text-center text-xs">
                <div>No Ticket</div>
              </div>
            );
          } else if (
            singleTourneyId?.registeredPlayerCount ===
            singleTourneyId?.maxPlayers
          ) {
            return (
              <div className="border-b-main rounded-200 flex min-h-[24px] min-w-[111px] flex-col justify-center border-1 px-3 py-1 text-center text-xs">
                <div>FULL</div>
              </div>
            );
          }
          return (
            <Register
              key={singleTourneyId.tourneyId}
              singleTourneyId={singleTourneyId}
            />
          );
        case TOURNEY_OFFLINE_PAUSED:
        case TOURNEY_STATUS_LIVE:
          if (
            !singleTourneyId?.isAlreadyRegisteredTourney &&
            singleTourneyId?.lateRegistrationAllowed &&
            singleTourneyId?.lateRegistrationStatus !== 'FINISHED'
          ) {
            return (
              <Register
                singleTourneyId={singleTourneyId}
                isLateTicket={tourneyDetails?.checkUserHasTicket(
                  singleTourneyId?.tourneyId
                )}
                isLateReg={true}
              />
            );
          } else if (
            singleTourneyId?.tourneyPlayerStatus ===
            TOURNEY_PLAYER_STATUS_RE_ENTRY
          ) {
            return (
              <div className="border-b-main rounded-200 flex min-h-[24px] min-w-[111px] flex-col justify-center border-1 px-3 py-1 text-center text-xs">
                Re-Entry
              </div>
            );
          } else if (
            singleTourneyId?.isAlreadyRegisteredTourney &&
            checkTourneyGameTableIsAvailable()
          ) {
            return (
              <div className="border-b-main rounded-200 flex min-h-[24px] min-w-[111px] flex-col justify-center border-1 px-3 py-1 text-center text-xs">
                Join Back
              </div>
            );
          } else {
            return (
              <div className="border-cta rounded-200 text-success flex h-[40px] min-w-[96px] flex-col items-center justify-center border-1 border-solid text-xs font-bold">
                <p>View</p>
                <p>Tourney</p>
              </div>
            );
          }

        case TOURNEY_STATUS_CANCELLED:
          return (
            <div className="rounded-200 flex min-h-[40px] min-w-[96px] flex-col items-center justify-center border border-solid border-black/30 bg-transparent text-xs">
              <div className="text-secondary text-center text-xs font-medium">
                {tourneyDetails.getTourneyBuyIn(singleTourneyId) === 0 ? (
                  <div className="text-completed">Free</div>
                ) : (
                  <div className="text-completed">{`₹${tourneyDetails.getTourneyBuyIn(
                    singleTourneyId
                  )}`}</div>
                )}
              </div>
              <div className="text-completed text-center text-xs font-bold">
                Register
              </div>
            </div>
          );

        case TOURNEY_STATUS_MISSED:
          return (
            <div className="rounded-200 flex min-h-[40px] min-w-[96px] flex-col items-center justify-center border border-solid border-black/30 bg-transparent text-xs">
              <div className="text-secondary text-center text-xs font-medium">
                {tourneyDetails.getTourneyBuyIn(singleTourneyId) === 0 ? (
                  <div className="text-completed">Free</div>
                ) : (
                  <div className="text-completed">{`₹${tourneyDetails.getTourneyBuyIn(
                    singleTourneyId
                  )}`}</div>
                )}
              </div>
              <div className="text-completed text-center text-xs font-bold">
                Register
              </div>
            </div>
          );
        case TOURNEY_STATUS_WON:
          if (
            singleTourneyId?.isFlightTourney &&
            !singleTourneyId?.isFlightParentTourney
          ) {
            return (
              <div
                className={`min-h-[24px] min-w-[111px] text-xs font-bold ${
                  singleTourneyId?.tourneyIdentifier === 'completedTourneys'
                    ? 'text-completed border border-black/30'
                    : 'border-b-main border-1'
                } rounded-200 flex flex-col justify-center px-3 py-1 text-center`}
              >
                Qualified
              </div>
            );
          } else {
            return (
              <div
                className={`min-h-[24px] min-w-[111px] text-xs font-bold ${
                  singleTourneyId?.tourneyIdentifier === 'completedTourneys'
                    ? 'text-completed border border-black/30'
                    : 'border-b-main border'
                } rounded-200 flex flex-col justify-center px-3 py-1 text-center`}
              >
                <div>You Won</div>
              </div>
            );
          }
        case TOURNEY_STATUS_COMPLETED:
          return (
            <div className="rounded-200 flex min-h-[40px] min-w-[96px] flex-col items-center justify-center border border-solid border-black/30 bg-transparent text-xs">
              <div className="text-secondary text-center text-xs font-medium">
                {tourneyDetails.getTourneyBuyIn(singleTourneyId) === 0 ? (
                  <div className="text-completed">Free</div>
                ) : (
                  <div className="text-completed">{`₹${tourneyDetails.getTourneyBuyIn(
                    singleTourneyId
                  )}`}</div>
                )}
              </div>
              <div className="text-completed text-center text-xs font-bold">
                Register
              </div>
            </div>
          );
        default:
          return null;
      }
    },
    [singleTourneyId]
  );

  if (!singleTourneyId) {
    return null;
  }

  const handleTourneyClick = (tourneyId) => {
    tourneyDetails.navigateToTourneyDetails(tourneyId);
  };

  const basic = singleTourneyId?.tourneyCardA23?.type === 'basic';
  const special =
    singleTourneyId?.tourneyCardA23?.type === 'special' ||
    (singleTourneyId?.tourneyCardA23?.type === 'featured' &&
      status !== 'megaTourney'); //oneshot
  const featured =
    singleTourneyId?.tourneyCardA23?.type === 'featured' &&
    status === 'megaTourney'; //mega

  return (
    <div
      className={`rounded-300 flex-1 ${
        singleTourneyId?.familyName?.visibility === true ? 'mt-[20px]' : 'mt-3'
      } from-foreground to-surface border-b-surface relative border bg-gradient-to-r from-0% ${
        (singleTourneyId?.tourneyStatus === TOURNEY_STATUS_CANCELLED ||
          singleTourneyId?.tourneyStatus === TOURNEY_STATUS_COMPLETED ||
          singleTourneyId?.tourneyIdentifier === 'completedTourneys') &&
        !open
          ? 'opacity-60'
          : ''
      } `}
    >
      {/* Family tag */}
      {singleTourneyId?.familyName?.visibility === true && (
        <div
          className={`rounded-200 absolute top-[-8px] left-3 z-10 flex h-[20px] max-w-[80px] items-center justify-center px-2`}
          style={{
            backgroundColor: singleTourneyId?.familyName?.backgroundColorA23,
            color: singleTourneyId?.familyName?.textColorA23
          }}
          title={singleTourneyId?.familyName?.nameA23}
        >
          <p className="overflow-hidden text-xs font-bold text-ellipsis whitespace-nowrap">
            {singleTourneyId?.familyName?.nameA23}
          </p>
        </div>
      )}

      <div onClick={() => handleTourneyClick(singleTourneyId?.tourneyId)}>
        <div className="text-secondary flex justify-between rounded-t-md">
          {basic && !special && !featured && (
            <div
              className={`flex h-[30px] items-center justify-between text-xs ${
                singleTourneyId?.familyName?.nameA23
                  ? 'pt-[18px] pl-[11px]'
                  : 'pt-[9px] pl-3'
              }`}
            >
              <div className="text-primary text-xs font-bold">
                {singleTourneyId?.tourneyName}
              </div>
            </div>
          )}

          {featured && (
            <div className="w-full">
              <div className="rounded-t-300 relative w-full">
                <div className="rounded-t-300 from-background/0 to-background absolute h-full w-full bg-linear-to-b" />
                <div className="rounded-t-300 h-[100px] w-auto">
                  <img
                    src={singleTourneyId?.tourneyCardA23?.featureImageURL}
                    className="rounded-t-300 h-[100px] w-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = gameAssets?.tourneyMega;
                    }}
                  />
                </div>
                <div className="text-secondary absolute bottom-2 left-4 z-20 text-xs font-bold">
                  {singleTourneyId?.tourneyName}
                </div>
              </div>
            </div>
          )}

          {special && (
            <div
              className="rounded-t-300 flex w-full flex-row items-center justify-between pr-2 pl-3"
              style={{
                background: `linear-gradient(to right, ${
                  singleTourneyId?.tourneyCardA23?.primaryColor || '#042a33'
                }, ${
                  singleTourneyId?.tourneyCardA23?.secondaryColor || '#042a33'
                })`
              }}
            >
              <div className="my-2 text-xs font-bold">
                {singleTourneyId?.tourneyName}
              </div>

              {singleTourneyId?.tourneyCardA23?.specialImageURL && (
                <div className="h-[30px] w-[45px]">
                  <img
                    className="h-full w-full object-cover"
                    src={singleTourneyId?.tourneyCardA23?.specialImageURL}
                    alt="Special tourney"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-row items-center justify-between px-3 py-2 text-base">
          <div>
            <div
              className={`flex items-center gap-1 text-base font-bold ${
                singleTourneyId?.tourneyStatus === TOURNEY_STATUS_CANCELLED ||
                singleTourneyId?.tourneyStatus === TOURNEY_STATUS_COMPLETED ||
                singleTourneyId?.tourneyIdentifier === 'completedTourneys'
                  ? 'text-completed'
                  : 'text-focus'
              }`}
            >
              {singleTourneyId?.tournamentType === 'Satellite' ? (
                <img
                  src={gameAssets.SilverTicket}
                  alt="silver ticket"
                  className="mr-1 ml-0.5"
                />
              ) : (
                <img
                  className="rounded-900"
                  src={gameAssets.rupeePrize}
                  alt="rupee prize"
                />
              )}

              <div>
                {singleTourneyId?.isFlightTourney ? (
                  <div id={singleTourneyId?.tourneyId}>
                    <div>
                      {singleTourneyId?.isFlightParentTourney ? (
                        <div>
                          {singleTourneyId?.breakEven
                            ? singleTourneyId?.dynamicPoolPrize > 0
                              ? `₹ ${formatAmountWithCommas(
                                  singleTourneyId?.dynamicPoolPrize
                                )}`
                              : ''
                            : singleTourneyId?.prizePool > 0
                              ? `₹ ${formatAmountWithCommas(
                                  singleTourneyId?.prizePool
                                )}`
                              : ''}

                          {singleTourneyId?.prizePoolTickets > 0 && (
                            <div className="flex flex-row items-center justify-center">
                              {((singleTourneyId?.breakEven &&
                                singleTourneyId?.dynamicPoolPrize > 0) ||
                                singleTourneyId?.prizePool > 0) && (
                                <div className="mx-0.5 text-xs font-bold">
                                  +
                                </div>
                              )}
                              {((singleTourneyId?.breakEven &&
                                singleTourneyId?.dynamicPoolPrize >= 0) ||
                                singleTourneyId?.prizePool >= 0) && (
                                <div className="items-centers flex flex-row items-end justify-center font-bold">
                                  <div className="text-base">
                                    {singleTourneyId?.prizePoolTickets}
                                  </div>
                                  <div
                                    className={`font-bold ${
                                      (singleTourneyId?.breakEven &&
                                        singleTourneyId?.dynamicPoolPrize <=
                                          0) ||
                                      (!singleTourneyId?.breakEven &&
                                        singleTourneyId?.prizePool <= 0)
                                        ? 'text-base'
                                        : 'text-xs'
                                    }ml-1 mb-0.5`}
                                  >
                                    {singleTourneyId?.prizePoolTickets === 1
                                      ? 'Ticket'
                                      : 'Tickets'}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          {singleTourneyId?.prizePool > 0
                            ? `₹ ${formatAmountWithCommas(
                                singleTourneyId?.prizePool
                              )}`
                            : singleTourneyId?.parentTourneyName}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    id={singleTourneyId.tourneyId}
                    className="bg-red-300x font-bolds flex flex-row items-center"
                  >
                    {/* Prize pool amount */}
                    {singleTourneyId?.breakEven
                      ? singleTourneyId?.dynamicPoolPrize > 0
                        ? `₹ ${formatAmountWithCommas(
                            singleTourneyId?.dynamicPoolPrize
                          )}`
                        : ''
                      : singleTourneyId?.prizePool > 0
                        ? `₹ ${formatAmountWithCommas(
                            singleTourneyId?.prizePool
                          )}`
                        : ''}

                    {/* Tickets display */}
                    {singleTourneyId?.prizePoolTickets > 0 && (
                      <div className="flex flex-row items-center justify-center">
                        {((singleTourneyId?.breakEven &&
                          singleTourneyId?.dynamicPoolPrize > 0) ||
                          singleTourneyId?.prizePool > 0) && (
                          <span className="mx-0.5 text-xs font-bold">+</span>
                        )}
                        {((singleTourneyId?.breakEven &&
                          singleTourneyId?.dynamicPoolPrize >= 0) ||
                          singleTourneyId?.prizePool >= 0) && (
                          <div className="items-centers flex flex-row items-end justify-center font-bold">
                            <div className="text-base">
                              {singleTourneyId?.prizePoolTickets}
                            </div>
                            <div
                              className={`${
                                (singleTourneyId?.breakEven &&
                                  singleTourneyId?.dynamicPoolPrize <= 0) ||
                                (!singleTourneyId?.breakEven &&
                                  singleTourneyId?.prizePool <= 0)
                                  ? 'text-sm'
                                  : 'text-xs'
                              } mb-0.5 ml-0.5`}
                            >
                              {singleTourneyId?.prizePoolTickets === 1
                                ? 'Ticket'
                                : 'Tickets'}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`justify-betweens mt-1 ml-1.5 flex items-center gap-3 ${
                singleTourneyId?.tourneyStatus === TOURNEY_STATUS_CANCELLED ||
                singleTourneyId?.tourneyStatus === TOURNEY_STATUS_COMPLETED ||
                singleTourneyId?.tourneyIdentifier === 'completedTourneys'
                  ? 'text-completed'
                  : 'text-secondary'
              }`}
            >
              <img
                src={gameAssets.userIcon}
                alt="Number of players"
                height={12}
                width={12}
              />
              <div className="text-xs font-bold">
                {singleTourneyId?.registeredPlayerCount} Seats
              </div>
            </div>
          </div>

          {(() => {
            const cta = getTourneyTileDynamicCtaOrStatus(
              singleTourneyId?.tourneyStatus
            );
            return <div>{cta}</div>;
          })()}
        </div>
      </div>

      <div className="rounded-b-300 from-foreground to-foreground/0 flex flex-row items-center justify-between bg-linear-to-l px-3 py-2">
        <div
          className="flex flex-row gap-1"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {TOURNEY_CARD_TYPES.map((tab, idx) =>
            tab ? (
              <div
                key={idx}
                className={`text-2xs min-h-[20px] rounded px-2 py-1 font-bold ${
                  tab === gameType
                    ? 'text-info bg-accent'
                    : 'text-secondary bg-background/30'
                }`}
              >
                {tab}
              </div>
            ) : null
          )}
        </div>

        <BottomModal open={open} onClose={() => setOpen(false)}>
          <div className="from-foreground to-background rounded-t-xl bg-linear-to-b">
            <div className="sticky top-0 z-10 rounded-t-xl px-4 pt-6 pb-2 text-base font-bold">
              Icons Legend
            </div>
            <div
              className="flex-1 overflow-y-auto px-4 pb-6"
              style={{ maxHeight: 'calc(80vh - 56px)' }}
            >
              {TOURNEY_LEGENDS.map((legend) => (
                <TourneyLegendItem
                  key={legend.code}
                  code={legend.code}
                  label={legend.label}
                  description={legend.description}
                />
              ))}
            </div>
          </div>
        </BottomModal>

        <div className="flex flex-row items-center gap-2">
          <div className="text-primary text-xs font-normal">
            {status === 'live' &&
            singleTourneyId?.isAlreadyRegisteredTourney === true ? (
              <div className="flex flex-row items-center gap-2">
                <div className="bg-success rounded-900 h-1.5 w-1.5" />
                <div className="text-secondary">Running</div>
              </div>
            ) : status === 'live' &&
              singleTourneyId?.isAlreadyRegisteredTourney === false ? (
              <div className="text-secondary">Missed</div>
            ) : status === 'completedTourneys' ? (
              singleTourneyId?.tourneyStatus === 'CANCELLED' ? (
                <div className="text-secondary">Cancelled</div>
              ) : (
                <div className="text-secondary">Finished</div>
              )
            ) : (
              <TourneyTimer
                tourneyStartTime={singleTourneyId?.tourneyStartTime}
                lateRegEndTime={singleTourneyId?.lateRegEndTime}
                lateRegistrationAllowed={
                  singleTourneyId?.lateRegistrationAllowed
                }
                lateRegEnded={singleTourneyId?.lateRegEnded}
                showLateRegCountdown={isLateReg}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default A23TourneyTile;
